import React from 'react'
import { Card, CardContent, Typography, Box, Button, Stack, Grid, CircularProgress } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import StarIcon from '@mui/icons-material/Star'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { BillingPlan } from '../../services/api'
import { PlanCardFeatureItem } from '../../services/paymentsApi'
import {
  extractPlanFeatureItems,
  calculateGst,
  formatINR,
} from '../../utils/paymentUtils'

interface PlanCardProps {
  plan: BillingPlan
  onSelectPlan: (planType: string) => void
  loading?: boolean
}

const getPlanIconDetails = (iconName?: string, isFeatured?: boolean) => {
  const normalizedName = (iconName || '').toLowerCase().trim()

  // 1. DB icon_name mapping (whatsapp_icon vs ai_icon)
  if (normalizedName === 'whatsapp_icon') {
    return {
      icon: <WhatsAppIcon sx={{ fontSize: 34, color: '#25D366' }} />,
      bg: '#DCFCE7',
    }
  }

  if (normalizedName === 'ai_icon') {
    return {
      icon: <AutoAwesomeIcon sx={{ fontSize: 34, color: '#2563EB' }} />,
      bg: '#EFF6FF',
    }
  }

  return isFeatured
    ? { icon: <AutoAwesomeIcon sx={{ fontSize: 34, color: '#2563EB' }} />, bg: '#EFF6FF' }
    : { icon: <WhatsAppIcon sx={{ fontSize: 34, color: '#25D366' }} />, bg: '#DCFCE7' }
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  onSelectPlan,
  loading = false,
}) => {
  const meta = plan.display_metadata || {}
  const isFeatured = meta.mode === 'featured'
  const price = meta.base_amount_subunits ? meta.base_amount_subunits / 100 : (plan.amount_subunits ? plan.amount_subunits / 100 : 0)
  const originalPrice = meta.original_amount_subunits ? meta.original_amount_subunits / 100 : null
  const gst = calculateGst(price)

  const rawButtonText = meta.button_text || `Select ${meta.title || plan.plan_type.toUpperCase()}`
  const buttonText = `${rawButtonText} (${gst.formattedTotal})`
  const featureItems: PlanCardFeatureItem[] = extractPlanFeatureItems(plan)
  const displayedFeatures = featureItems.slice(0, 12)
  const col1Features = displayedFeatures.slice(0, 6)
  const col2Features = displayedFeatures.slice(6, 12)

  const iconDetails = getPlanIconDetails(meta.icon_name, isFeatured)

  const discountPercent = originalPrice && originalPrice > price
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : null

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderRadius: '24px',
        bgcolor: '#ffffff',
        transition: 'all 0.2s ease-in-out',
        ...(isFeatured
          ? {
              border: '1.5px solid #2563EB',
              boxShadow: '0px 8px 30px rgba(37, 99, 235, 0.08)',
            }
          : {
              border: '1.5px solid #E2E8F0',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.02)',
              '&:hover': {
                borderColor: '#CBD5E1',
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.05)',
              },
            }),
      }}
    >
      <CardContent sx={{ p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Header section with Badge */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              bgcolor: iconDetails.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {iconDetails.icon}
          </Box>

          {meta.badge && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: '#2563EB',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.75rem',
                letterSpacing: '0.04em',
                px: 1.5,
                py: 0.6,
                borderRadius: '20px',
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
              }}
            >
              <StarIcon sx={{ fontSize: 14, color: '#ffffff' }} />
              <span>{String(meta.badge).toUpperCase()}</span>
            </Box>
          )}
        </Stack>

        <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#0F172A', letterSpacing: '-0.01em', mb: 0.5 }}>
          {meta.title || plan.plan_type.toUpperCase()}
        </Typography>
        {meta.subtitle && (
          <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', lineHeight: 1.4, minHeight: { sm: 36 }, mb: 1 }}>
            {meta.subtitle}
          </Typography>
        )}

        <Box sx={{ borderBottom: '1px solid #F1F5F9', my: 2.5 }} />

        {/* Pricing section */}
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '2.15rem', sm: '2.5rem' }, color: '#0F172A', lineHeight: 1, letterSpacing: '-0.02em' }}>
            {formatINR(price, false)}
          </Typography>

          {(originalPrice !== null || discountPercent !== null) && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75, mb: 3 }}>
              {originalPrice && (
                <Typography sx={{ fontSize: '0.9rem', color: '#94A3B8', textDecoration: 'line-through', fontWeight: 500 }}>
                  {formatINR(originalPrice, false)}
                </Typography>
              )}
              {discountPercent !== null && (
                <Box
                  sx={{
                    display: 'inline-block',
                    bgcolor: isFeatured ? '#DBEAFE' : '#DCFCE7',
                    color: isFeatured ? '#1E40AF' : '#166534',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    px: 1,
                    py: 0.2,
                    borderRadius: '6px',
                  }}
                >
                  {discountPercent}% OFF
                </Box>
              )}
            </Stack>
          )}

          <Typography sx={{ color: '#475569', fontWeight: 600, fontSize: '0.875rem', mt: 1 }}>
            + 18% GST ({gst.formattedGst})
          </Typography>

          {meta.price_note && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#64748B', fontSize: '0.75rem' }}>
              {meta.price_note}
            </Typography>
          )}
        </Box>

        <Box sx={{ borderBottom: '1px solid #F1F5F9', my: 2.5 }} />

        {/* Features section */}
        {displayedFeatures.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', color: '#64748B', mb: 2, textTransform: 'uppercase' }}>
              {meta.features_header || 'FEATURES INCLUDED'}
            </Typography>

            <Grid container spacing={col2Features.length > 0 ? 2 : 0} sx={{ mb: 4, flexGrow: 1 }}>
              <Grid item xs={12} sm={col2Features.length > 0 ? 6 : 12}>
                <Stack spacing={1.75}>
                  {col1Features.map((item, idx) => (
                    <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                      {item.included ? (
                        <CheckCircleIcon sx={{ fontSize: 20, color: isFeatured ? '#2563EB' : '#16A34A', mt: '2px', flexShrink: 0 }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: 20, color: '#CBD5E1', mt: '2px', flexShrink: 0 }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.9rem',
                          color: item.included ? '#1E293B' : '#94A3B8',
                          fontWeight: item.included ? 500 : 400,
                          lineHeight: 1.4,
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>

              {col2Features.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.75}>
                    {col2Features.map((item, idx) => (
                      <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                        {item.included ? (
                          <CheckCircleIcon sx={{ fontSize: 20, color: isFeatured ? '#2563EB' : '#16A34A', mt: '2px', flexShrink: 0 }} />
                        ) : (
                          <CancelIcon sx={{ fontSize: 20, color: '#CBD5E1', mt: '2px', flexShrink: 0 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.9rem',
                            color: item.included ? '#1E293B' : '#94A3B8',
                            fontWeight: item.included ? 500 : 400,
                            lineHeight: 1.4,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Action Button */}
        <Button
          fullWidth
          variant={isFeatured ? 'contained' : 'outlined'}
          disabled={loading}
          onClick={() => onSelectPlan(plan.plan_type)}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            py: 1.4,
            borderRadius: '12px',
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '0.95rem',
            ...(isFeatured
              ? {
                  bgcolor: '#2563EB',
                  color: '#ffffff',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#1D4ED8',
                    boxShadow: 'none',
                  },
                }
              : {
                  borderColor: '#16A34A',
                  color: '#16A34A',
                  borderWidth: '1.5px',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: '#15803D',
                    color: '#15803D',
                    borderWidth: '1.5px',
                    bgcolor: '#F0FDF4',
                  },
                }),
          }}
        >
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  )
}
